'use client';

import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { FileDown, Eye, ChevronUp, ChevronDown, AlertCircle } from 'lucide-react';
import { useRole } from '@/context/RoleContext';

interface PayrollRow {
  id: string;
  workerName: string;
  niNumber: string;
  taxCode: string;
  niCategory: string;
  grossPay: number;
  payeTax: number;
  eeNI: number;
  erNI: number;
  pensionEe: number;
  pensionEr: number;
  studentLoan: number;
  netPay: number;
  status: 'approved' | 'draft' | 'pending';
  flag?: string;
}

// Backend integration point: replace with API call to /api/payroll-runs/PR-2026-W18/lines
const PAYROLL_ROWS: PayrollRow[] = [
  { id: 'pr-w18-001', workerName: 'Amara Okafor', niNumber: 'NJ 47 23 91 A', taxCode: '1257L', niCategory: 'A', grossPay: 481.25, payeTax: 64.20, eeNI: 28.50, erNI: 23.10, pensionEe: 24.06, pensionEr: 14.44, studentLoan: 0, netPay: 364.49, status: 'approved' },
  { id: 'pr-w18-002', workerName: 'Cian Walsh', niNumber: 'SN 82 14 55 C', taxCode: '1257L', niCategory: 'A', grossPay: 525.00, payeTax: 78.40, eeNI: 32.10, erNI: 26.20, pensionEe: 26.25, pensionEr: 15.75, studentLoan: 0, netPay: 388.25, status: 'pending', flag: 'OT: 4.5h' },
  { id: 'pr-w18-003', workerName: 'Priya Nair', niNumber: 'KP 33 67 12 B', taxCode: '1257L', niCategory: 'B', grossPay: 343.20, payeTax: 32.10, eeNI: 0, erNI: 12.40, pensionEe: 17.16, pensionEr: 10.30, studentLoan: 0, netPay: 293.94, status: 'approved' },
  { id: 'pr-w18-004', workerName: 'David Mensah', niNumber: 'TM 91 44 78 A', taxCode: '1100L', niCategory: 'A', grossPay: 350.00, payeTax: 54.80, eeNI: 18.90, erNI: 14.20, pensionEe: 17.50, pensionEr: 10.50, studentLoan: 0, netPay: 258.80, status: 'approved', flag: 'Tax code query' },
  { id: 'pr-w18-005', workerName: 'Fatima Byrne', niNumber: 'PW 56 31 04 A', taxCode: '1257L', niCategory: 'A', grossPay: 528.00, payeTax: 79.20, eeNI: 32.50, erNI: 26.60, pensionEe: 26.40, pensionEr: 15.84, studentLoan: 0, netPay: 389.90, status: 'approved' },
  { id: 'pr-w18-006', workerName: 'Grace Adeyemi', niNumber: 'LR 72 88 36 A', taxCode: '1257L', niCategory: 'A', grossPay: 320.32, payeTax: 24.40, eeNI: 14.20, erNI: 10.40, pensionEe: 16.02, pensionEr: 9.61, studentLoan: 0, netPay: 265.70, status: 'approved' },
  { id: 'pr-w18-007', workerName: 'Hassan Clarke', niNumber: 'MN 19 52 67 A', taxCode: '1257L', niCategory: 'A', grossPay: 468.75, payeTax: 61.30, eeNI: 27.20, erNI: 22.10, pensionEe: 23.44, pensionEr: 14.06, studentLoan: 22.00, netPay: 334.81, status: 'pending' },
  { id: 'pr-w18-008', workerName: 'Ifeoma Obi', niNumber: 'QS 64 27 89 A', taxCode: '1257L', niCategory: 'A', grossPay: 550.00, payeTax: 85.00, eeNI: 34.80, erNI: 28.40, pensionEe: 27.50, pensionEr: 16.50, studentLoan: 0, netPay: 402.70, status: 'pending', flag: 'WTD: 47.5h' },
  { id: 'pr-w18-009', workerName: 'James Whitfield', niNumber: 'BR 48 93 15 C', taxCode: '1257L', niCategory: 'C', grossPay: 182.40, payeTax: 0, eeNI: 0, erNI: 0, pensionEe: 9.12, pensionEr: 5.47, studentLoan: 0, netPay: 173.28, status: 'approved' },
  { id: 'pr-w18-010', workerName: 'Kezia Mwangi', niNumber: 'XN 37 61 43 A', taxCode: '1257L', niCategory: 'A', grossPay: 375.00, payeTax: 42.80, eeNI: 20.10, erNI: 16.20, pensionEe: 18.75, pensionEr: 11.25, studentLoan: 0, netPay: 293.35, status: 'approved' },
];

const TOTALS = {
  grossPay: PAYROLL_ROWS.reduce((s, r) => s + r.grossPay, 0),
  payeTax: PAYROLL_ROWS.reduce((s, r) => s + r.payeTax, 0),
  eeNI: PAYROLL_ROWS.reduce((s, r) => s + r.eeNI, 0),
  erNI: PAYROLL_ROWS.reduce((s, r) => s + r.erNI, 0),
  pensionEe: PAYROLL_ROWS.reduce((s, r) => s + r.pensionEe, 0),
  pensionEr: PAYROLL_ROWS.reduce((s, r) => s + r.pensionEr, 0),
  studentLoan: PAYROLL_ROWS.reduce((s, r) => s + r.studentLoan, 0),
  netPay: PAYROLL_ROWS.reduce((s, r) => s + r.netPay, 0),
};

type SortKey = 'workerName' | 'grossPay' | 'payeTax' | 'netPay';

export default function PayrollTable() {
  const [sortKey, setSortKey] = useState<SortKey>('workerName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const { permissions } = useRole();

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...PAYROLL_ROWS].sort((a, b) => {
    let av: string | number = a[sortKey];
    let bv: string | number = b[sortKey];
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const fmt = (n: number) => `£${n.toFixed(2)}`;

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1">
      <ChevronUp size={9} className={sortKey === col && sortDir === 'asc' ? 'text-primary' : 'text-muted-foreground/40'} />
      <ChevronDown size={9} className={sortKey === col && sortDir === 'desc' ? 'text-primary' : 'text-muted-foreground/40'} />
    </span>
  );

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-[14px] font-600 text-foreground">Worker Payroll Breakdown</h3>
          <p className="text-[12px] text-muted-foreground mt-0.5">Showing 10 of 47 workers · Week 18 · PR-2026-W18</p>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />
          2 rows flagged for review
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[13px] min-w-[1200px]">
          <thead>
            <tr className="border-b border-border bg-secondary/40">
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('workerName')}>
                Worker <SortIcon col="workerName" />
              </th>
              <th className="px-4 py-3 text-left font-600 text-[11px] uppercase tracking-wider text-muted-foreground">NI Number</th>
              <th className="px-4 py-3 text-center font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Tax Code</th>
              <th className="px-4 py-3 text-center font-600 text-[11px] uppercase tracking-wider text-muted-foreground">NI Cat.</th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('grossPay')}>
                Gross Pay <SortIcon col="grossPay" />
              </th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('payeTax')}>
                PAYE Tax <SortIcon col="payeTax" />
              </th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Ee NI</th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Er NI</th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Pension Ee</th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Pension Er</th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground">SL</th>
              <th className="px-4 py-3 text-right font-600 text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('netPay')}>
                Net Pay <SortIcon col="netPay" />
              </th>
              <th className="px-4 py-3 text-center font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-center font-600 text-[11px] uppercase tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((row) => (
              <tr
                key={row.id}
                className={`group transition-colors duration-100 hover:bg-secondary/50 ${row.flag ? 'bg-warning/3' : ''}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-700 text-primary">
                        {row.workerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <span className="font-500 text-foreground whitespace-nowrap block">{row.workerName}</span>
                      {row.flag && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <AlertCircle size={10} className="text-warning" />
                          <span className="text-[10px] text-warning font-500">{row.flag}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-[12px] text-muted-foreground whitespace-nowrap">{row.niNumber}</td>
                <td className="px-4 py-3 text-center">
                  <span className="font-mono text-[12px] font-600 text-foreground bg-secondary px-2 py-0.5 rounded">{row.taxCode}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-secondary text-[11px] font-700 text-foreground">{row.niCategory}</span>
                </td>
                <td className="px-4 py-3 text-right font-mono font-600 text-foreground tabular-nums">{fmt(row.grossPay)}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px] text-accent tabular-nums">{row.payeTax > 0 ? fmt(row.payeTax) : '—'}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px] text-muted-foreground tabular-nums">{row.eeNI > 0 ? fmt(row.eeNI) : '—'}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px] text-muted-foreground tabular-nums">{row.erNI > 0 ? fmt(row.erNI) : '—'}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px] text-success tabular-nums">{fmt(row.pensionEe)}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px] text-success tabular-nums">{fmt(row.pensionEr)}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px] text-muted-foreground tabular-nums">{row.studentLoan > 0 ? fmt(row.studentLoan) : '—'}</td>
                <td className="px-4 py-3 text-right font-mono font-700 text-foreground tabular-nums text-[13px]">{fmt(row.netPay)}</td>
                <td className="px-4 py-3 text-center">
                  <StatusBadge variant={row.status} size="sm" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button
                      className="relative p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-all duration-150 group/btn"
                      aria-label="View payslip"
                    >
                      <Eye size={14} />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-foreground text-card text-[10px] rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 pointer-events-none transition-opacity z-20">
                        View payslip
                      </span>
                    </button>
                    {permissions.canDownloadPayslips && (
                      <button
                        className="relative p-1.5 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all duration-150 group/btn"
                        aria-label="Download payslip PDF"
                      >
                        <FileDown size={14} />
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-foreground text-card text-[10px] rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 pointer-events-none transition-opacity z-20">
                          Download payslip PDF
                        </span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>

          {/* Totals footer */}
          <tfoot>
            <tr className="border-t-2 border-border bg-secondary/60">
              <td className="px-4 py-3 font-700 text-[12px] text-foreground" colSpan={4}>
                Totals — 10 workers shown
              </td>
              <td className="px-4 py-3 text-right font-mono font-700 text-foreground tabular-nums">{fmt(TOTALS.grossPay)}</td>
              <td className="px-4 py-3 text-right font-mono font-700 text-accent tabular-nums">{fmt(TOTALS.payeTax)}</td>
              <td className="px-4 py-3 text-right font-mono font-700 text-muted-foreground tabular-nums">{fmt(TOTALS.eeNI)}</td>
              <td className="px-4 py-3 text-right font-mono font-700 text-muted-foreground tabular-nums">{fmt(TOTALS.erNI)}</td>
              <td className="px-4 py-3 text-right font-mono font-700 text-success tabular-nums">{fmt(TOTALS.pensionEe)}</td>
              <td className="px-4 py-3 text-right font-mono font-700 text-success tabular-nums">{fmt(TOTALS.pensionEr)}</td>
              <td className="px-4 py-3 text-right font-mono font-700 text-muted-foreground tabular-nums">{fmt(TOTALS.studentLoan)}</td>
              <td className="px-4 py-3 text-right font-mono font-700 text-foreground tabular-nums text-[14px]">{fmt(TOTALS.netPay)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-secondary/20">
        <p className="text-[12px] text-muted-foreground">
          Showing <span className="font-500 text-foreground">10</span> of <span className="font-500 text-foreground">47</span> workers
        </p>
        <div className="flex items-center gap-1">
          {['1', '2', '3', '4', '5'].map((p) => (
            <button
              key={`payroll-page-${p}`}
              className={`w-7 h-7 rounded text-[12px] font-500 transition-all duration-150 ${
                p === '1' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-muted-foreground">Rows per page</span>
          <select className="text-[12px] font-500 bg-card border border-border rounded px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/30">
            <option>10</option>
            <option>25</option>
            <option>50</option>
          </select>
        </div>
      </div>
    </div>
  );
}